import React, { useEffect, useState, useCallback } from 'react';
import { dashboardService } from '../services/dashboardService';
import {
  Clock,
  FileText,
  FolderOpen,
  Users,
  Briefcase,
  MessageSquare,
  AlertCircle
} from 'lucide-react';

const ActivityFeed = ({ limit = 10, showHeader = true }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardService.getRecentActivities(limit);
      setActivities(response.data || []);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError(err.response?.data?.detail || 'Failed to load recent activities');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const getActivityIcon = (activityType, entityType) => {
    switch (entityType) {
      case 'case':
        return <Briefcase className="w-4 h-4 text-yellow-600" />;
      case 'client':
        return <Users className="w-4 h-4 text-green-600" />;
      case 'project':
        return <FolderOpen className="w-4 h-4 text-purple-600" />;
      case 'document':
        return <FileText className="w-4 h-4 text-yellow-600" />;
      case 'communication':
        return <MessageSquare className="w-4 h-4 text-indigo-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - activityTime.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    } else {
      return activityTime.toLocaleDateString();
    }
  };

  const getEntityLink = (activity) => {
    switch (activity.entity_type) {
      case 'case':
        return `/cases/${activity.entity_id}`;
      case 'client':
        return `/clients/${activity.entity_id}`;
      case 'project':
        return `/projects/${activity.entity_id}`;
      case 'document':
        return `/documents/${activity.entity_id}`;
      case 'communication':
        return `/communications`;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {showHeader && (
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        )}
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {showHeader && (
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        )}
        <div className="flex items-center justify-center h-40 text-red-600">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {showHeader && (
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        )}
        <div className="text-center py-8 text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>No recent activity</p>
          <p className="text-sm mt-1">Your recent actions will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {showHeader && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
        </div>
      )}
      <div className="divide-y divide-gray-200">
        {activities.map((activity, index) => {
          const link = getEntityLink(activity);
          const ActivityContent = (
            <div className="flex items-start space-x-3 px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex-shrink-0 mt-1">
                {getActivityIcon(activity.activity_type, activity.entity_type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">{activity.description}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {getTimeAgo(activity.timestamp)}
                </p>
              </div>
            </div>
          );

          return link ? (
            <a
              key={`${activity.entity_id}-${index}`}
              href={link}
              className="block cursor-pointer"
            >
              {ActivityContent}
            </a>
          ) : (
            <div key={`${activity.entity_id}-${index}`}>
              {ActivityContent}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityFeed;
